import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Globe, 
  Eye, 
  FileText, 
  TrendingUp, 
  Settings, 
  Calendar,
  Shield,
  Activity,
  BarChart3,
  CreditCard,
  Edit3,
  Plus,
  X,
  Save,
  Trash2,
  Ban,
  CheckCircle,
  Search,
  Filter,
  ExternalLink,
  Archive
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAdminTheme, getThemeClasses } from '../contexts/AdminTheme';
import AdminThemeSwitcher from './AdminThemeSwitcher';
import ActivityDashboard from './admin/ActivityDashboard';
import { getPreviewUrl } from '../utils/siteUrls';
import UsageDashboard from './admin/UsageDashboard';

const AdminDashboard = () => {
  const { theme } = useAdminTheme();
  const themeClasses = getThemeClasses(theme);
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    email: '',
    password: '',
    planId: '',
    role: 'user'
  });

  // Sites filter states
  const [sitesFilter, setSitesFilter] = useState({
    search: '',
    status: '',
    page: 1
  });
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesPagination, setSitesPagination] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'sites') {
      loadSites();
    }
  }, [activeTab, sitesFilter]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [statsResponse, usersResponse, plansResponse] = await Promise.all([
        fetch('/api/admin/stats', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/admin/users?limit=10', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/admin/plans', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (statsResponse.ok && usersResponse.ok && plansResponse.ok) {
        const statsData = await statsResponse.json();
        const usersData = await usersResponse.json();
        const plansData = await plansResponse.json();
        
        setStats(statsData);
        setUsers(usersData.users);
        setPlans(plansData);
        
        // Initialize create form with first plan if not already set
        if (plansData.length > 0 && !createUserForm.planId) {
          setCreateUserForm(prev => ({
            ...prev,
            planId: plansData[0]._id
          }));
        }
      } else {
        if (statsResponse.status === 403 || usersResponse.status === 403) {
          toast.error('Admin access required');
        } else {
          toast.error('Failed to load dashboard data');
        }
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success('User role updated successfully');
        loadDashboardData(); // Reload data
      } else {
        toast.error('Failed to update user role');
      }
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const updateUserPlan = async (userId, newPlanId) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/plan`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ planId: newPlanId })
      });

      if (response.ok) {
        toast.success('User plan updated successfully');
        loadDashboardData(); // Reload data
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update user plan');
      }
    } catch (error) {
      toast.error('Failed to update user plan');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(createUserForm)
      });

      if (response.ok) {
        toast.success('User created successfully');
        setShowCreateUser(false);
        setCreateUserForm({
          name: '',
          email: '',
          password: '',
          planId: '',
          role: 'user'
        });
        loadDashboardData(); // Reload data
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
    }
  };

  const loadSites = async () => {
    setSitesLoading(true);
    try {
      const params = new URLSearchParams({
        page: sitesFilter.page.toString(),
        limit: '20'
      });
      
      if (sitesFilter.search) {
        params.append('search', sitesFilter.search);
      }
      if (sitesFilter.status) {
        params.append('status', sitesFilter.status);
      }

      const response = await fetch(`/api/admin/sites?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSites(data.sites);
        setSitesPagination(data.pagination);
      } else {
        toast.error('Failed to load sites');
      }
    } catch (error) {
      console.error('Failed to load sites:', error);
      toast.error('Failed to load sites');
    } finally {
      setSitesLoading(false);
    }
  };

  const updateSiteStatus = async (siteId, newStatus) => {
    try {
      const response = await fetch(`/api/admin/sites/${siteId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Site ${newStatus === 'published' ? 'published' : newStatus === 'archived' ? 'archived' : 'unpublished'} successfully`);
        loadSites(); // Reload sites
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update site status');
      }
    } catch (error) {
      toast.error('Failed to update site status');
    }
  };

  const deleteSite = async (siteId) => {
    if (!window.confirm('Are you sure you want to delete this site? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/sites/${siteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Site deleted successfully');
        loadSites(); // Reload sites
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to delete site');
      }
    } catch (error) {
      toast.error('Failed to delete site');
    }
  };

  const suspendUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to suspend ${userName}? This will archive all their sites.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/sites/owner/${userId}/suspend`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ reason: 'admin_action' })
      });

      if (response.ok) {
        toast.success('User suspended successfully');
        loadSites(); // Reload sites
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to suspend user');
      }
    } catch (error) {
      toast.error('Failed to suspend user');
    }
  };

  const unsuspendUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to unsuspend ${userName}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/sites/owner/${userId}/unsuspend`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('User unsuspended successfully');
        loadSites(); // Reload sites
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to unsuspend user');
      }
    } catch (error) {
      toast.error('Failed to unsuspend user');
    }
  };

  const resetCreateUserForm = () => {
    setCreateUserForm({
      name: '',
      email: '',
      password: '',
      planId: plans.length > 0 ? plans[0]._id : '',
      role: 'user'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${themeClasses.page}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className={`mt-4 ${themeClasses.text}`}>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${themeClasses.page}`}>
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className={`text-xl font-semibold ${themeClasses.heading} mb-2`}>Access Denied</h2>
          <p className={themeClasses.text}>You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color = "blue", trend = null }) => (
    <div className={`${themeClasses.card} p-6`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm font-medium ${themeClasses.textMuted}`}>{title}</p>
          <p className={`text-2xl font-bold ${themeClasses.heading}`}>{value}</p>
          {trend && (
            <p className="text-sm text-green-600 mt-1">
              <TrendingUp className="w-4 h-4 inline mr-1" />
              +{trend} today
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className={themeClasses.page}>
      {/* Header */}
      <header className={themeClasses.header}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-2xl ${themeClasses.heading}`}>Admin Dashboard</h1>
              <p className={themeClasses.text}>System overview and management</p>
            </div>
            <div className="flex items-center space-x-4">
              <AdminThemeSwitcher />
              <Link
                to="/admin/plans"
                className={themeClasses.button}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Manage Plans
              </Link>
              <Link
                to="/admin/settings"
                className={themeClasses.buttonSecondary}
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Link>
              <Link
                to="/"
                className={themeClasses.buttonSecondary}
              >
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'sites', label: 'Sites', icon: Globe },
              { id: 'activity', label: 'Activity', icon: Activity },
              { id: 'usage', label: 'Usage', icon: TrendingUp }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : `${themeClasses.textMuted} ${themeClasses.hover}`
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard 
                title="Total Users" 
                value={stats.overview.totalUsers} 
                icon={Users} 
                color="blue"
                trend={stats.overview.usersToday}
              />
              <StatCard 
                title="Total Sites" 
                value={stats.overview.totalSites} 
                icon={Globe} 
                color="green"
                trend={stats.overview.sitesToday}
              />
              <StatCard 
                title="Published Sites" 
                value={stats.overview.publishedSites} 
                icon={Eye} 
                color="purple"
              />
              <StatCard 
                title="Draft Sites" 
                value={stats.overview.draftSites} 
                icon={FileText} 
                color="orange"
              />
            </div>

            {/* Plan Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className={`${themeClasses.card} p-6`}>
                <h3 className={`text-lg font-semibold ${themeClasses.heading} mb-4`}>Plan Distribution</h3>
                <div className="space-y-4">
                  {Object.entries(stats.planDistribution).map(([planSlug, planData], index) => {
                    const colors = ['bg-gray-400', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-red-500'];
                    return (
                      <div key={planSlug} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`w-3 h-3 rounded-full mr-3 ${colors[index % colors.length]}`}></div>
                          <span className={`capitalize font-medium ${themeClasses.text}`}>
                            {typeof planData === 'object' ? planData.name : planSlug}
                          </span>
                        </div>
                        <span className={`text-lg font-bold ${themeClasses.heading}`}>
                          {typeof planData === 'object' ? planData.count : planData}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Weekly Activity Chart */}
              <div className={`${themeClasses.card} p-6`}>
                <h3 className={`text-lg font-semibold ${themeClasses.heading} mb-4`}>Weekly Activity</h3>
                <div className="space-y-2">
                  {stats.weeklyActivity.map((day, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className={themeClasses.text}>{formatDate(day.date)}</span>
                      <div className="flex items-center space-x-4">
                        <span className="text-blue-600">{day.users} users</span>
                        <span className="text-green-600">{day.sites} sites</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className={`${themeClasses.card} p-6`}>
                <h3 className={`text-lg font-semibold ${themeClasses.heading} mb-4`}>Recent Users</h3>
                <div className="space-y-3">
                  {stats.recentUsers.map(user => (
                    <div key={user._id} className="flex items-center justify-between">
                      <div>
                        <div className={`font-medium ${themeClasses.heading}`}>{user.name}</div>
                        <div className={`text-sm ${themeClasses.textMuted}`}>{user.email}</div>
                      </div>
                      <div className="text-right">
                        <div className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role}
                        </div>
                        <div className={`text-xs ${themeClasses.textMuted} mt-1`}>{formatDate(user.createdAt)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`${themeClasses.card} p-6`}>
                <h3 className={`text-lg font-semibold ${themeClasses.heading} mb-4`}>Recent Sites</h3>
                <div className="space-y-3">
                  {stats.recentSites.map(site => (
                    <div key={site._id} className="flex items-center justify-between">
                      <div>
                        <div className={`font-medium ${themeClasses.heading}`}>{site.name}</div>
                        <div className={`text-sm ${themeClasses.textMuted}`}>by {site.owner?.name}</div>
                      </div>
                      <div className="text-right">
                        <div className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          site.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {site.status}
                        </div>
                        <div className={`text-xs ${themeClasses.textMuted} mt-1`}>{formatDate(site.createdAt)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Create User Modal */}
            {showCreateUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className={`${themeClasses.card} rounded-lg shadow-xl w-full max-w-md mx-4`}>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-lg font-semibold ${themeClasses.heading}`}>Create New User</h3>
                      <button
                        onClick={() => {
                          setShowCreateUser(false);
                          resetCreateUserForm();
                        }}
                        className={themeClasses.iconButton}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <form onSubmit={handleCreateUser} className="space-y-4">
                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.label}`}>
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={createUserForm.name}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                          className={`w-full ${themeClasses.input}`}
                          required
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.label}`}>
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={createUserForm.email}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                          className={`w-full ${themeClasses.input}`}
                          required
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.label}`}>
                          Password
                        </label>
                        <input
                          type="password"
                          value={createUserForm.password}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                          className={`w-full ${themeClasses.input}`}
                          minLength="6"
                          required
                        />
                        <p className={`text-xs ${themeClasses.textMuted} mt-1`}>Minimum 6 characters</p>
                      </div>

                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.label}`}>
                          Plan
                        </label>
                        <select
                          value={createUserForm.planId}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, planId: e.target.value })}
                          className={`w-full ${themeClasses.input}`}
                          required
                        >
                          <option value="">Select a plan</option>
                          {plans.map(plan => (
                            <option key={plan._id} value={plan._id}>
                              {plan.name} {plan.pricing.monthly > 0 && `($${plan.pricing.monthly}/mo)`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className={`block text-sm font-medium ${themeClasses.label}`}>
                          Role
                        </label>
                        <select
                          value={createUserForm.role}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, role: e.target.value })}
                          className={`w-full ${themeClasses.input}`}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>

                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateUser(false);
                            resetCreateUserForm();
                          }}
                          className={themeClasses.buttonSecondary}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className={`${themeClasses.button} flex items-center`}
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Create User
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            <div className={themeClasses.card}>
              <div className={`p-6 border-b ${themeClasses.border}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-lg font-semibold ${themeClasses.heading}`}>User Management</h3>
                    <p className={themeClasses.text}>Manage user accounts and permissions</p>
                  </div>
                  <button
                    onClick={() => {
                      resetCreateUserForm();
                      setShowCreateUser(true);
                    }}
                    className={`${themeClasses.button} flex items-center`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create User
                  </button>
                </div>
              </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={themeClasses.secondary}>
                  <tr>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>User</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Plan</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Role</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Joined</th>
                    <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={`${themeClasses.card} divide-y ${themeClasses.border}`}>
                  {users.map(user => (
                    <tr key={user._id} className={themeClasses.hover}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className={`text-sm font-medium ${themeClasses.heading}`}>{user.name}</div>
                          <div className={`text-sm ${themeClasses.textMuted}`}>{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingUser === user._id ? (
                          <select
                            value={user.plan?._id || ''}
                            onChange={(e) => {
                              updateUserPlan(user._id, e.target.value);
                              setEditingUser(null);
                            }}
                            className={`text-sm ${themeClasses.input} px-2 py-1`}
                            onBlur={() => setEditingUser(null)}
                            autoFocus
                          >
                            {plans.map(plan => (
                              <option key={plan._id} value={plan._id}>{plan.name}</option>
                            ))}
                          </select>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                              user.plan?.slug === 'free' ? 'bg-gray-100 text-gray-800' :
                              user.plan?.slug === 'basic' ? 'bg-green-100 text-green-800' :
                              user.plan?.slug === 'pro' ? 'bg-blue-100 text-blue-800' :
                              user.plan?.slug === 'business' ? 'bg-purple-100 text-purple-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {user.plan?.name || 'No Plan'}
                            </span>
                            <button
                              onClick={() => setEditingUser(user._id)}
                              className={themeClasses.iconButton}
                              title="Change plan"
                            >
                              <Edit3 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={user.role}
                          onChange={(e) => updateUserRole(user._id, e.target.value)}
                          className="text-sm border border-gray-600 rounded px-2 py-1"
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${themeClasses.textMuted}`}>
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button className="text-blue-600 hover:text-blue-900">Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        )}

        {/* Sites Tab */}
        {activeTab === 'sites' && (
          <div className="space-y-6">
            <div className={themeClasses.card}>
              <div className={`p-6 border-b ${themeClasses.border}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className={`text-lg font-semibold ${themeClasses.heading}`}>Site Management</h3>
                    <p className={themeClasses.text}>Manage all sites on the platform</p>
                  </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search sites, domains, or business names..."
                        value={sitesFilter.search}
                        onChange={(e) => setSitesFilter({ ...sitesFilter, search: e.target.value, page: 1 })}
                        className={`w-full pl-10 ${themeClasses.input}`}
                      />
                    </div>
                  </div>
                  <div className="sm:w-48">
                    <select
                      value={sitesFilter.status}
                      onChange={(e) => setSitesFilter({ ...sitesFilter, status: e.target.value, page: 1 })}
                      className={`w-full ${themeClasses.input}`}
                    >
                      <option value="">All Status</option>
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Sites Table */}
              <div className="overflow-x-auto">
                {sitesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className={`ml-3 ${themeClasses.text}`}>Loading sites...</span>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className={themeClasses.secondary}>
                      <tr>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Site</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Owner</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Plan</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Status</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Created</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className={`${themeClasses.card} divide-y ${themeClasses.border}`}>
                      {sites.map(site => (
                        <tr key={site._id} className={themeClasses.hover}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className={`text-sm font-medium ${themeClasses.heading}`}>
                                {site.name}
                              </div>
                              <div className={`text-sm ${themeClasses.textMuted}`}>
                                {site.domain ? (
                                  <a 
                                    href={getPreviewUrl(site.domain)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 flex items-center"
                                  >
                                    {site.domain}
                                    <ExternalLink className="w-3 h-3 ml-1" />
                                  </a>
                                ) : (
                                  <span className="text-gray-400">No domain</span>
                                )}
                              </div>
                              {site.businessInfo?.name && (
                                <div className={`text-xs ${themeClasses.textMuted}`}>
                                  {site.businessInfo.name}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className={`text-sm font-medium ${themeClasses.heading}`}>
                                {site.owner?.name}
                              </div>
                              <div className={`text-sm ${themeClasses.textMuted}`}>
                                {site.owner?.email}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                              site.owner?.plan?.slug === 'free' ? 'bg-gray-100 text-gray-800' :
                              site.owner?.plan?.slug === 'basic' ? 'bg-green-100 text-green-800' :
                              site.owner?.plan?.slug === 'pro' ? 'bg-blue-100 text-blue-800' :
                              site.owner?.plan?.slug === 'business' ? 'bg-purple-100 text-purple-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {site.owner?.plan?.name || 'No Plan'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                              site.status === 'published' ? 'bg-green-100 text-green-800' :
                              site.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {site.status}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${themeClasses.textMuted}`}>
                            {formatDate(site.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              {/* Status Actions */}
                              {site.status === 'draft' && (
                                <button
                                  onClick={() => updateSiteStatus(site._id, 'published')}
                                  className="text-green-600 hover:text-green-900 p-1"
                                  title="Publish site"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              )}
                              {site.status === 'published' && (
                                <button
                                  onClick={() => updateSiteStatus(site._id, 'draft')}
                                  className="text-yellow-600 hover:text-yellow-900 p-1"
                                  title="Unpublish site"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              )}
                              {site.status !== 'archived' && (
                                <button
                                  onClick={() => updateSiteStatus(site._id, 'archived')}
                                  className="text-gray-600 hover:text-gray-900 p-1"
                                  title="Archive site"
                                >
                                  <Archive className="w-4 h-4" />
                                </button>
                              )}
                              
                              {/* User Actions */}
                              {site.owner?.subscription?.status !== 'suspended' ? (
                                <button
                                  onClick={() => suspendUser(site.owner._id, site.owner.name)}
                                  className="text-orange-600 hover:text-orange-900 p-1"
                                  title="Suspend user"
                                >
                                  <Ban className="w-4 h-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => unsuspendUser(site.owner._id, site.owner.name)}
                                  className="text-green-600 hover:text-green-900 p-1"
                                  title="Unsuspend user"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              )}
                              
                              {/* Delete Action */}
                              <button
                                onClick={() => deleteSite(site._id)}
                                className="text-red-600 hover:text-red-900 p-1"
                                title="Delete site"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Pagination */}
                {sitesPagination && sitesPagination.totalPages > 1 && (
                  <div className={`px-6 py-4 border-t ${themeClasses.border} flex items-center justify-between`}>
                    <div className={`text-sm ${themeClasses.textMuted}`}>
                      Showing {((sitesPagination.currentPage - 1) * 20) + 1} to {Math.min(sitesPagination.currentPage * 20, sitesPagination.totalSites)} of {sitesPagination.totalSites} sites
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSitesFilter({ ...sitesFilter, page: sitesFilter.page - 1 })}
                        disabled={sitesFilter.page <= 1}
                        className={`px-3 py-1 text-sm border rounded ${sitesFilter.page <= 1 ? 'opacity-50 cursor-not-allowed' : themeClasses.buttonSecondary}`}
                      >
                        Previous
                      </button>
                      <span className={`text-sm ${themeClasses.text}`}>
                        Page {sitesPagination.currentPage} of {sitesPagination.totalPages}
                      </span>
                      <button
                        onClick={() => setSitesFilter({ ...sitesFilter, page: sitesFilter.page + 1 })}
                        disabled={sitesFilter.page >= sitesPagination.totalPages}
                        className={`px-3 py-1 text-sm border rounded ${sitesFilter.page >= sitesPagination.totalPages ? 'opacity-50 cursor-not-allowed' : themeClasses.buttonSecondary}`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {!sitesLoading && sites.length === 0 && (
                  <div className="text-center py-12">
                    <Globe className={`w-12 h-12 ${themeClasses.textMuted} mx-auto mb-4`} />
                    <p className={themeClasses.textMuted}>
                      {sitesFilter.search || sitesFilter.status ? 'No sites match your filters' : 'No sites found'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <ActivityDashboard themeClasses={themeClasses} />
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <UsageDashboard themeClasses={themeClasses} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;